import { defineStore } from 'pinia';
import { Applicant } from '~/composables/types/Applicant';
import { useMutation } from '@tanstack/vue-query';
import { ApiResponse, Client, CreatePayload } from '~/composables/types/Integration';
import { Application } from '~/composables/types/Application';
import { globalFetchOptions } from '~/utils/helpers';

export const useApplicantIntegrationStore = defineStore('applicant-integration', () => {
  const wellSkySearch = () => {
    return useMutation((formData: { firstName?: string; lastName?: string; dateOfBirth?: string; clientID?: string }) =>
      $fetch<ApiResponse<Client[]>>(`/integrations/wellsky/clients`, {
        ...globalFetchOptions(),
        method: 'GET',
        params: formData,
        retry: false,
      })
    );
  };

  const getClientByHousehold = (household: number) => {
    return $fetch<ApiResponse<Client[]>>(`/integrations/wellsky/households/${household}`, {
      ...globalFetchOptions(),
      method: 'GET',
      retry: false,
    });
  };

  const getAllHousehold = (applicationId: string) => {
    return $fetch<ApiResponse<Client[]>>(`/applications/${applicationId}/integration/household-members`, {
      ...globalFetchOptions(),
      method: 'GET',
      retry: false,
    });
  };

  const getProviderName = (locationId: string) => {
    return $fetch<ApiResponse<Client[]>>(`/integrations/wellsky/locationprovider/${locationId}`, {
      ...globalFetchOptions(),
      method: 'GET',
      retry: false,
    });
  };

  const arAssociatedLocation = (arId: string) => {
    return $fetch<ApiResponse<Client[]>>(`/assistance-requests/${arId}/distributions/associated-location-id`, {
      ...globalFetchOptions(),
      method: 'GET',
      retry: false,
    });
  };

  const create = (application: Application | null) => {
    let householdType = 'Other';
    const step = application?.formkitForm?.steps?.find((step) => step.stepId === '66539288-bb83-4be7-a723-8e3170a0e812');
    if (step) {
      const householdTypeField = step.fields.find((field) => field.name === 'householdtype');
      if (householdTypeField && step?.data && step?.data[0]['householdtype'] !== undefined) {
        householdType = householdTypeField?.options.find(
          (option: any) => step?.data && option.value === step?.data[0]['householdtype']
        )?.label;
      }
    }

    return useMutation((formData: CreatePayload) => {
      if (!formData.applicant.oldClientId || formData.applicant.oldClientId === '') {
        formData.applicant.oldClientId = null;
      }
      return $fetch<any>(`/applications/${application?.id}/integration/household-members/create`, {
        ...globalFetchOptions(),
        method: 'POST',
        body: {
          ...formData,
          householdType,
        },
        retry: false,
      });
    });
  };

  const householdCreate = () => {
    return useMutation(
      (
        formData: {
          clientId: string;
          headOfHouseholdStatus: string;
          headOfHouseholdRelationship: string;
          dateJoined: string;
        }[]
      ) =>
        $fetch<any>(`/integrations/wellsky/households`, {
          ...globalFetchOptions(),
          method: 'POST',
          body: {
            householdType: 'Other',
            householdClients: formData,
          },
          retry: false,
        })
    );
  };

  const hhMatchNeeded = ref(false);

  const updateHHMatchNeeded = (value: boolean) => {
    hhMatchNeeded.value = value;
  };

  const getServiceCodes = (id: string) => {
    return $fetch<ApiResponse<Client[]>>(`/assistance-types/${id}/service-codes`, {
      ...globalFetchOptions(),
      method: 'GET',
    });
  };
  const showApplicantMatchDialog = ref(false);
  const matchDialogOpen = ref(false);
  const matchDialogOpen2 = ref(false);

  // Closes the whole Wellsky match flow (root MatchDialog + nested ApplicantMatchDialog).
  // Used when an integration request fails and the user dismisses the error toast.
  const closeMatchDialogs = () => {
    console.log('Closing match dialogs');
    matchDialogOpen.value = false;
    showApplicantMatchDialog.value = false;
    matchDialogOpen2.value = false;
  };

  return {
    wellSkySearch,
    create,
    householdCreate,
    getClientByHousehold,
    getAllHousehold,
    hhMatchNeeded,
    updateHHMatchNeeded,
    getProviderName,
    arAssociatedLocation,
    getServiceCodes,
    showApplicantMatchDialog,
    matchDialogOpen,
    closeMatchDialogs,
    matchDialogOpen2,
  };
});
